import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { pr as api } from '../services/api';
import { useFiltresVentes, usePanneau, useCanal, basculer, eurCourt } from './ventesUtils';
import FiltresPanel from './FiltresPanel';
import AvancementMois from './AvancementMois';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles, sectionsGroupes, basculerGroupe } from './regroupements';
import './Ventes.css';
import './Stock.css';       // st-tools / st-seg des réglages d'affichage
import './VentesJour.css';  // vj-*, rg-* : même gabarit que les autres quotidiens

// ─── PR — Le mois au jour le jour ────────────────────────────────────────────
// Même ossature que VN/VO et APV. Ce qui lui est propre : les DEUX CANAUX.
// Le comptoir (ventes du magasin) pèse 64 % du PR et n'apparaît nulle part
// ailleurs dans le BI ; l'atelier est la part qu'on retrouve en « dont pièces »
// du rapport APV. Les colonnes les séparent, toujours.

const nfr = new Intl.NumberFormat('fr-FR');
const eur = (v) => (v == null ? '—' : `${nfr.format(Math.round(v))} €`);
const pct = (v) => (v == null ? '—' : `${nfr.format(Math.round(v * 10) / 10)} %`);
const jourCourt = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }) : '');
const estWeekend = (d) => [0, 6].includes(new Date(`${d}T12:00:00`).getDay());

// Mêmes helpers que les autres rapports quotidiens : un mois long en français
// et la liste des mois précédents.
const moisLong = (ym) => {
  const [a, m] = ym.split('-');
  return new Date(Number(a), Number(m) - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};
const moisPrecedents = (n) => {
  const out = []; const d = new Date(); d.setDate(1);
  for (let i = 0; i < n; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
};

const MOIS_RECENTS = moisPrecedents(2);
const MOIS_CHOIX = [
  { code: '', label: 'Mois en cours' },
  { code: MOIS_RECENTS[1], label: moisLong(MOIS_RECENTS[1]) },
];
const MOIS_AUTRES = moisPrecedents(13).slice(2).map((m) => ({ code: m, label: moisLong(m) }));

// Colonnes du tableau quotidien, en deux groupes : le jour, puis le cumul.
const COLS = [
  { c: 'ca', g: 'Le jour', lib: 'CA HT', f: (j) => j.ca, fmt: eur, fort: true },
  { c: 'comptoir', g: 'Le jour', lib: 'dont comptoir', f: (j) => j.comptoir, fmt: eur, discret: true },
  { c: 'atelier', g: 'Le jour', lib: 'dont atelier', f: (j) => j.atelier, fmt: eur, discret: true },
  { c: 'marge', g: 'Le jour', lib: 'Marge', f: (j) => j.marge, fmt: eur },
  { c: 'factures', g: 'Le jour', lib: 'Factures', f: (j) => j.factures, fmt: (v) => nfr.format(v) },
  { c: 'taux', g: 'Cumul du mois', lib: 'Taux de marge', f: (j) => j.taux, fmt: pct, discret: true },
  { c: 'panier', g: 'Cumul du mois', lib: 'Panier moyen', f: (j) => j.panier, fmt: eur, discret: true },
];
const groupes = (cols) => cols.reduce((acc, c) => {
  const d = acc[acc.length - 1];
  if (d && d.lib === c.g) d.n += 1; else acc.push({ lib: c.g, n: 1 });
  return acc;
}, []);

// Colonnes des blocs de répartition (concession et axes).
const REP = [
  { c: 'ca', lib: 'CA HT', f: (x) => x.ca, fmt: eur, fort: true },
  { c: 'comptoir', lib: 'dont comptoir', f: (x) => x.comptoir, fmt: eur, discret: true, soc: true },
  { c: 'atelier', lib: 'dont atelier', f: (x) => x.atelier, fmt: eur, discret: true, soc: true },
  { c: 'marge', lib: 'Marge', f: (x) => x.marge, fmt: eur },
  { c: 'taux', lib: 'Taux', f: (x) => x.taux, fmt: pct, discret: true },
  { c: 'factures', lib: 'Factures', f: (x) => x.factures, fmt: (v) => nfr.format(v) },
  { c: 'panier', lib: 'Panier moyen', f: (x) => x.panier, fmt: eur, discret: true },
];

const AXES_PR = [
  { c: 'marque', lib: 'marque', col: 'Marque' },
  { c: 'atelier', lib: 'atelier', col: 'Atelier' },
  { c: 'receptionnaire', lib: 'vendeur', col: 'Vendeur' },
];

export default function PrJour({ branche = 'auto-antilles' }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [dispo, setDispo] = useState([]);
  const [axe, setAxe] = useState('marque');
  const f = useFiltresVentes(dispo);
  const [ouvert, ouvrirPanneau] = usePanneau();
  const mois = f.moisStr;
  const societes = f.societesUrl;
  // Le canal est un PÉRIMÈTRE : il recale toute la page, comme la société.
  const { canal, choix } = useCanal(f);
  const unCanal = canal !== '';

  const charger = useCallback(async () => {
    setErreur('');
    try {
      const { data: d } = await api.jour({ mois, societes, canal, branche });
      setData(d); setDispo(d.societesDispo || []);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Chargement impossible'); setData(null);
    }
  }, [branche, mois, societes, canal]);
  useEffect(() => { charger(); }, [charger]);

  const feuilles = useMemo(
    () => (data?.societesCumul || []).map((s) => ({ ...s, libelle: s.societe, code: s.societeCode })), [data]);
  const axes = useMemo(() => axesDisponibles(feuilles), [feuilles]);
  // ⚠ Seules les mesures ADDITIVES se somment ; le taux et le panier moyen se
  // recalculent depuis ces sommes. Moyenner des taux de concessions de tailles
  // différentes donnerait un faux.
  const sommer = (membres) => {
    const t = membres.reduce((a, m) => ({
      ca: a.ca + m.ca, marge: a.marge + m.marge, factures: a.factures + m.factures,
      lignes: a.lignes + m.lignes, comptoir: a.comptoir + (m.comptoir || 0), atelier: a.atelier + (m.atelier || 0),
    }), { ca: 0, marge: 0, factures: 0, lignes: 0, comptoir: 0, atelier: 0 });
    return { ...t, taux: t.ca ? (t.marge / t.ca) * 100 : null, panier: t.factures ? t.ca / t.factures : null };
  };
  const maille = useRegroupement();
  const arbre = useMemo(
    () => construireArbre(feuilles, maille.niveau, sommer, maille.ouverts, (x) => x.ca),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feuilles, maille.niveau, maille.ouverts]);
  const AXE = AXES_PR.find((a) => a.c === axe) || AXES_PR[0];
  // Sur un canal unique, les colonnes « dont comptoir » / « dont atelier » ne
  // disent plus rien : l'une recopie le CA, l'autre est vide. On les retire.
  const REP_SOC = useMemo(() => REP.filter((c) => !unCanal || !c.soc), [unCanal]);
  const REP_AXE = useMemo(() => REP.filter((c) => !c.soc), []);
  const COLS_J = useMemo(() => COLS.filter((c) => !unCanal || !['comptoir', 'atelier'].includes(c.c)), [unCanal]);

  const Panneau = (
    <FiltresPanel
      ouvert={ouvert} onToggle={ouvrirPanneau}
      periodes={MOIS_CHOIX} periode={mois} onPeriode={(m) => f.setFiltre({ mois: m })}
      moisAutres={MOIS_AUTRES}
      groupes={sectionsGroupes(feuilles)}
      onToggleGroupe={(g) => f.setFiltre({ societes: basculerGroupe(g, f.selected, data?.societesDispo || dispo) })}
      societesDispo={data?.societesDispo || dispo} societesActives={f.selected}
      onToggleSociete={f.toggleSociete}
      onToutesSocietes={() => f.setFiltre({ societes: '' })}
      onAucuneSociete={() => f.setFiltre({ societes: '__aucune__' })}
      choix={choix}
      couleurDe={f.couleurDe}
      nbActifs={(mois ? 1 : 0) + (f.societesUrl ? 1 : 0) + (canal ? 1 : 0)}
      onReset={() => f.setFiltre({ societes: '', mois: '', canal: '' })}
    />
  );
  if (erreur) return <><main className="vt-page"><div className="vj-erreur">{erreur}</div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const C = data.cumul || {};
  const J = data.jours || [];
  // Jours ouvrés sans facture (2026-10-20) : présents à zéro dans le tableau,
  // ils ne comptent pas parmi les « jours facturés ».
  const N = J.filter((j) => !j.sansFacture).length;
  const iDernier = J.reduce((d, j, i) => (j.sansFacture ? d : i), -1);
  // « Par jour » = ÷ jours OUVRÉS écoulés (un jour ouvré sans facture compte).
  const joursOuvresEcoules = J.filter((j) => !estWeekend(j.jour)).length;
  const R = data.repere || { cible: 0, attendu: null, courbe: 'lineaire' };
  const lignesAxe = (data.parAxe || {})[axe] || [];

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div><h1>PR — Le mois au jour le jour</h1></div>
          <div className="vt-asof"><b>{moisLong(data.mois)}</b></div>
        </div>

        {unCanal && (
          <div className="vj-sel">
            <span>Périmètre limité au <b>{canal.toLowerCase()}</b> — tous les blocs le suivent.</span>
            <button type="button" className="vj-sel-x" onClick={() => f.setFiltre({ canal: '' })}>Voir les deux canaux</button>
          </div>
        )}

        {N === 0 ? (
          <div className="vj-vide">
            {data.vide
              ? 'Aucune société sélectionnée — cochez-en au moins une dans les filtres, à droite.'
              : 'Aucune facture de pièces sur ce mois.'}
          </div>
        ) : (
          <>
            <div className="vt-kpis" style={{ gridTemplateColumns: `repeat(${unCanal ? 4 : 6},1fr)` }}>
              <div className="vt-kpi">
                <div className="l">CA pièces{unCanal ? ` — ${canal.toLowerCase()}` : ''} — {N} j facturés</div>
                <div className="v">{eurCourt(C.ca)}</div>
                <div className="d vj-d">{nfr.format(C.lignes)} lignes</div>
              </div>
              {!unCanal && (
                <div className="vt-kpi">
                  <div className="l">dont comptoir</div>
                  <div className="v">{eurCourt(C.comptoir)}</div>
                  <div className="d vj-d">{C.ca ? Math.round((C.comptoir / C.ca) * 100) : 0} % du CA</div>
                </div>
              )}
              {!unCanal && (
                <div className="vt-kpi">
                  <div className="l">dont atelier</div>
                  <div className="v">{eurCourt(C.atelier)}</div>
                  <div className="d vj-d">pièces montées sur OR</div>
                </div>
              )}
              <div className="vt-kpi">
                <div className="l">Marge</div>
                <div className="v">{eurCourt(C.marge)}</div>
                <div className="d vj-d">{pct(C.taux)} du CA</div>
              </div>
              <div className="vt-kpi">
                <div className="l">Factures</div>
                <div className="v">{nfr.format(C.factures)}</div>
                <div className="d vj-d">{(C.factures / (joursOuvresEcoules || 1)).toFixed(0)} par jour</div>
              </div>
              <div className="vt-kpi">
                <div className="l">Panier moyen</div>
                <div className="v">{eur(C.panier)}</div>
                <div className="d vj-d">CA ÷ factures</div>
              </div>
            </div>

            {R.cible > 0 && (
              <div className="vt-card">
                <div className="vt-card-h"><h2>Où en est le mois</h2>
                  <span className="hint">{N} jour{N > 1 ? 's' : ''} facturé{N > 1 ? 's' : ''} · repère : le mois précédent, faute d'objectif saisi</span></div>
                <AvancementMois
                  realise={C.ca} cible={R.cible} attendu={R.attendu}
                  repere="mois précédent" article="du" fmt={eurCourt}
                  jours={`${N} jour${N > 1 ? 's' : ''} facturé${N > 1 ? 's' : ''}`}
                  estime={R.courbe === 'lineaire'} />
              </div>
            )}

            {(data.societesDispo || []).length > 1 && feuilles.length > 0 && (
              <div className="vt-card">
                <div className="vt-card-h">
                  <h2>Par concession — cumul du mois</h2>
                  <div className="st-tools">
                    <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axes} />
                  </div>
                </div>
                <div className="vj-scroll">
                  <table className="vj-tbl tbl-dense">
                    <thead><tr>
                      <th className="l">Concession</th>
                      {REP_SOC.map((col) => <th key={col.c} className={col.discret ? 'vj-th-discret' : ''}>{col.lib}</th>)}
                    </tr></thead>
                    <tbody>
                      {arbre.map((s) => {
                        const cible = s.feuille ? s.code : (s.membres || []).join(',');
                        return (
                          <tr key={s.cle} className={`tbl-clic rg-n${s.niveau}${f.societesUrl === cible ? ' actif' : ''}`}
                            onClick={() => f.setFiltre({ societes: basculer(f.societesUrl, cible) })}>
                            <td className="l vj-marque">
                              <span className="rg-in" style={{ width: s.niveau * 14 }} />
                              {s.feuille ? <span className="rg-chev vide" /> : (
                                <button type="button" className={`rg-btn${maille.ouverts.has(s.cle) ? ' on' : ''}`}
                                  title={maille.ouverts.has(s.cle) ? 'Replier' : 'Voir le détail par concession'}
                                  aria-label="Déplier"
                                  onClick={(e) => { e.stopPropagation(); maille.basculer(s.cle); }}>▶</button>
                              )}
                              {s.libelle || s.societe}
                              {!s.feuille && <span className="rg-nb">{s.nbEnfants}</span>}
                            </td>
                            {REP_SOC.map((col) => (
                              <td key={col.c} className={[col.discret ? 'vj-discret' : '', col.fort ? 'vj-fort' : ''].filter(Boolean).join(' ')}>
                                {col.fmt(col.f(s))}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot><tr>
                      <td className="l">Ensemble</td>
                      {REP_SOC.map((col) => (
                        <td key={col.c} className={col.discret ? 'vj-discret' : ''}>{col.fmt(col.f(C))}</td>
                      ))}
                    </tr></tfoot>
                  </table>
                </div>
              </div>
            )}

            <div className="vt-card">
              <div className="vt-card-h"><h2>Le mois jour par jour</h2>
                <span className="hint">les taux sont donnés en cumul depuis le 1<sup>er</sup></span></div>
              <div className="vj-scroll">
                <table className="vj-tbl tbl-dense">
                  <thead>
                    <tr className="vj-groupes">
                      <th className="l" />
                      {groupes(COLS_J).map((g, i) => <th key={i} colSpan={g.n}>{g.lib}</th>)}
                    </tr>
                    <tr>
                      <th className="l">{N} jours facturés</th>
                      {COLS_J.map((col) => (
                        <th key={col.c} className={col.discret ? 'vj-th-discret' : ''}>
                          {col.lib}{col.sous && <small>{col.sous}</small>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {J.map((j, i) => (
                      <tr key={j.jour}
                        className={`${i === iDernier ? 'tbl-courant' : ''}${estWeekend(j.jour) ? ' vj-weekend' : ''}${j.sansFacture ? ' aj-sans' : ''}`}>
                        <td className="l vj-jour">
                          <b>{jourCourt(j.jour)}</b>
                          {i === iDernier && <span className="vj-tag">dernier jour facturé</span>}
                          {j.sansFacture && <span className="vj-tag aj-zero">aucune facture</span>}
                          {estWeekend(j.jour) && <span className="vj-we">week-end</span>}
                        </td>
                        {COLS_J.map((col) => {
                          const v = col.f(j);
                          const cls = [col.discret ? 'vj-discret' : '', col.fort ? 'vj-fort' : ''].filter(Boolean).join(' ');
                          return <td key={col.c} className={cls}>{v == null ? '—' : col.fmt(v)}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="l">Total du mois</td>
                      {COLS_J.map((col) => (
                        <td key={col.c} className={col.discret ? 'vj-discret' : ''}>{col.fmt(col.f(C))}</td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="vt-card">
              <div className="vt-card-h">
                <h2>Le mois par {AXE.lib}</h2>
                <div className="st-tools">
                  <div className="st-seg">
                    {AXES_PR.map((a) => {
                      const off = !((data.parAxe || {})[a.c] || []).length;
                      return (
                        <button key={a.c} type="button" disabled={off}
                          className={axe === a.c ? 'on' : ''}
                          title={off ? `« ${a.col} » n'est renseigné sur aucune facture du mois` : ''}
                          onClick={() => setAxe(a.c)}>{a.lib}</button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="vj-scroll vj-scroll-borne">
                <table className="vj-tbl tbl-dense">
                  <thead><tr>
                    <th className="l">{AXE.col}</th>
                    {REP_AXE.map((col) => <th key={col.c} className={col.discret ? 'vj-th-discret' : ''}>{col.lib}</th>)}
                  </tr></thead>
                  <tbody>
                    {lignesAxe.map((x) => (
                      <tr key={x.nom}>
                        <td className="l vj-marque">{x.nom}</td>
                        {REP_AXE.map((col) => (
                          <td key={col.c} className={[col.discret ? 'vj-discret' : '', col.fort ? 'vj-fort' : ''].filter(Boolean).join(' ')}>
                            {col.fmt(col.f(x))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr>
                    <td className="l">Ensemble</td>
                    {REP_AXE.map((col) => (
                      <td key={col.c} className={col.discret ? 'vj-discret' : ''}>{col.fmt(col.f(C))}</td>
                    ))}
                  </tr></tfoot>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
      {Panneau}
    </>
  );
}
